// File: frontend/src/contexts/LanguageContext.js

import React, { createContext, useState, useContext, useEffect } from 'react';
import { STORAGE_KEYS } from '../config/constants';

const SUPPORTED_LOCALES = {
  'en-us': {
    name: 'English (USA)',
    languageCode: 'en',
    countryCode: 'us',
    flag: '🇺🇸'
  },
  'en-ca': {
    name: 'English (Canada)',
    languageCode: 'en',
    countryCode: 'ca',
    flag: '🇨🇦'
  },
  'es-mx': {
    name: 'Español (México)',
    languageCode: 'es',
    countryCode: 'mx',
    flag: '🇲🇽'
  },
  'fr-ca': {
    name: 'Français (Canada)',
    languageCode: 'fr',
    countryCode: 'ca',
    flag: '🇨🇦'
  }
};

const DEFAULT_LOCALE = 'en-ca';

const LanguageContext = createContext();

/**
 * Parse simple HTML-like tags in translation strings
 * Example: "Hello <bold>world</bold>" with tagMap { bold: BoldComponent }
 * 
 * @param {string} text - The translation text with HTML-like tags
 * @param {Object} tagMap - Map of tag names to React components
 * @returns {Array|string} - Array of React elements or original string
 */
export const parseTranslationHTML = (text, tagMap = {}) => {
  if (!text || typeof text !== 'string') return text;
  
  // If no tags found, return as-is
  if (!text.includes('<') || !text.includes('>')) return text;
  
  const parts = [];
  let currentIndex = 0;
  let key = 0;
  
  // Regex to find tags like <tagname>content</tagname>
  const tagRegex = /<(\w+)>(.*?)<\/\1>/g;
  let match;
  
  while ((match = tagRegex.exec(text)) !== null) {
    // Add text before the tag
    if (match.index > currentIndex) {
      const textBefore = text.substring(currentIndex, match.index);
      if (textBefore) parts.push(textBefore);
    }
    
    const tagName = match[1];
    const content = match[2];
    const Component = tagMap[tagName];
    
    if (Component) {
      // Render with the mapped component
      parts.push(
        <Component key={`tag-${key++}`}>
          {content}
        </Component>
      );
    } else {
      // No component mapped, just use the content
      parts.push(content);
    }
    
    currentIndex = match.index + match[0].length;
  }
  
  // Add any remaining text after the last tag
  if (currentIndex < text.length) {
    const textAfter = text.substring(currentIndex);
    if (textAfter) parts.push(textAfter);
  }
  
  // Return array if we parsed tags, otherwise original text
  return parts.length > 0 ? parts : text;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    
    const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    if (savedLanguage && SUPPORTED_LOCALES[savedLanguage]) {
      return savedLanguage;
    }
    
    const browserLang = navigator.language.toLowerCase();
    const matchedLocale = Object.keys(SUPPORTED_LOCALES).find(locale => 
      browserLang.startsWith(locale) || browserLang.startsWith(SUPPORTED_LOCALES[locale].languageCode)
    );
    
    return matchedLocale || DEFAULT_LOCALE;
  });

  const [translations, setTranslations] = useState({});

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const translationModule = await import(`../locales/${language}.json`);
        setTranslations(translationModule.default);
      } catch (error) {
        console.error(`Failed to load translations for ${language}:`, error);
        if (language !== DEFAULT_LOCALE) {
          try {
            const fallbackModule = await import(`../locales/${DEFAULT_LOCALE}.json`);
            setTranslations(fallbackModule.default);
          } catch (fallbackError) {
            console.error('Failed to load fallback translations:', fallbackError);
          }
        }
      }
    };

    loadTranslations();
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (SUPPORTED_LOCALES[newLanguage]) {
      setLanguage(newLanguage);
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, newLanguage);
      document.documentElement.lang = SUPPORTED_LOCALES[newLanguage].languageCode;
    }
  };

  useEffect(() => {
    document.documentElement.lang = SUPPORTED_LOCALES[language].languageCode;
  }, [language]);

  /**
   * Translation function
   * 
   * @param {string} key - Translation key (e.g., 'auth.signin.title')
   * @param {string} defaultText - Fallback English text
   * @param {Object} params - Optional parameters for variable substitution
   * @returns {string} - Translated text
   */
  const t = (key, defaultText = '', params = null) => {
    // Use translation from file, fallback to defaultText, then to key
    let text = translations[key] || defaultText || key;
    
    // Handle parameter substitution for variables like {{name}}
    if (params) {
      Object.keys(params).forEach(paramKey => {
        text = text.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), params[paramKey]);
      });
    }
    
    return text;
  };

  const value = {
    language,
    changeLanguage,
    t,
    parseHTML: parseTranslationHTML,
    supportedLanguages: Object.keys(SUPPORTED_LOCALES),
    languageInfo: SUPPORTED_LOCALES[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { LanguageContext };

export default LanguageContext;